import type maplibregl from 'maplibre-gl';
import { AIRCRAFT_MODELS_LAYER_ID } from './aircraftModelLayer';

export const MAP_MAX_PITCH = 80;

/** Pitch below this is treated as top-down (2D) for the toggle label. */
export const MAP_TOP_DOWN_PITCH = 2;

export const TOPOGRAPHY_LAYER_ID = 'hills';

export const ALTITUDE_LAYER_IDS = ['aircraft-altitude-shadow', 'aircraft-altitude-pillar'] as const;

export const GROUND_ICON_LAYER_ID = 'aircraft-icon';

export const AIRCRAFT_LABEL_LAYER_ID = 'aircraft-label';

export const GROUND_MARKER_LAYER_IDS = ['aircraft-icon', 'aircraft-body'] as const;

export const FLIGHT_PATHS_LAYER_ID = 'flight-paths';

export const VESSEL_LAYER_IDS = [
  'vessel-paths',
  'vessel-trail-glow',
  'vessel-trail',
  'vessel-body',
  'vessel-icon',
  'vessel-label',
  'vessel-selected',
] as const;

let map: maplibregl.Map | null = null;
let bearing = 0;
let pitch = 0;
let topographyEnabled = true;
let heightHologramsEnabled = true;
let aircraft3DModelsEnabled = true;
let groundIconEnabled = true;
/** True when ground icons were auto-disabled on 3D entry (restore on exit if unchanged). */
let groundIconAutoDisabledFor3D = false;
let trajectoryLinesEnabled = true;
let trajectoryFrom3DAircraft = true;
let aircraftEnabled = true;
let vesselsEnabled = false;
const bearingListeners = new Set<(b: number) => void>();
const pitchListeners = new Set<(p: number) => void>();
const topographyListeners = new Set<(enabled: boolean) => void>();
const heightHologramsListeners = new Set<(enabled: boolean) => void>();
const aircraft3DModelsListeners = new Set<(enabled: boolean) => void>();
const groundIconListeners = new Set<(enabled: boolean) => void>();
const trajectoryLinesListeners = new Set<(enabled: boolean) => void>();
const trajectorySourceListeners = new Set<() => void>();
const vesselsListeners = new Set<(enabled: boolean) => void>();
const aircraftListeners = new Set<(enabled: boolean) => void>();

function notifyTrajectorySource() {
  for (const fn of trajectorySourceListeners) fn();
}

/** Why the trajectory-source toggle is locked, if at all. */
export type TrajectorySourceOverride = 'trajectory-off' | 'ground-off' | 'models-off' | 'top-down' | null;

function trajectorySourceOverride(p = pitch): TrajectorySourceOverride {
  if (!trajectoryLinesEnabled) return 'trajectory-off';
  if (p < MAP_TOP_DOWN_PITCH) return 'top-down';
  if (!aircraft3DModelsEnabled) return 'models-off';
  if (!groundIconEnabled) return 'ground-off';
  return null;
}

function effectiveTrajectoryFrom3D(p = pitch): boolean {
  if (!trajectoryLinesEnabled) return false;
  if (p < MAP_TOP_DOWN_PITCH) return false;
  if (!aircraft3DModelsEnabled) return false;
  if (!groundIconEnabled) return true;
  return trajectoryFrom3DAircraft;
}

function notifyBearing() {
  for (const fn of bearingListeners) fn(bearing);
}

function notifyPitch() {
  for (const fn of pitchListeners) fn(pitch);
}

function notifyTopography() {
  for (const fn of topographyListeners) fn(topographyEnabled);
}

function applyTopographyVisibility() {
  if (!map?.getLayer(TOPOGRAPHY_LAYER_ID)) return;
  map.setLayoutProperty(TOPOGRAPHY_LAYER_ID, 'visibility', topographyEnabled ? 'visible' : 'none');
}

function notifyHeightHolograms() {
  for (const fn of heightHologramsListeners) fn(heightHologramsEnabled);
}

function applyHeightHologramsVisibility() {
  const visible = heightHologramsEnabled && pitch >= MAP_TOP_DOWN_PITCH ? 'visible' : 'none';
  for (const id of ALTITUDE_LAYER_IDS) {
    if (map?.getLayer(id)) map.setLayoutProperty(id, 'visibility', visible);
  }
}

function applyAircraft3DModelsVisibility() {
  const visible = aircraft3DModelsEnabled && pitch >= MAP_TOP_DOWN_PITCH ? 'visible' : 'none';
  // The 3D model layer is a MapLibre custom layer; visibility toggles its
  // render calls. Wrapped in case a host treats custom-layer layout props
  // differently. The layer also self-gates inside render() as a fallback.
  if (map?.getLayer(AIRCRAFT_MODELS_LAYER_ID)) {
    try {
      map.setLayoutProperty(AIRCRAFT_MODELS_LAYER_ID, 'visibility', visible);
    } catch {
      // Custom layer visibility not supported here; render() gate handles it.
    }
  }
}

function applyGroundIconVisibility() {
  // In 2D mode the toggle has no effect — ground icons always show.
  const visible = pitch < MAP_TOP_DOWN_PITCH || groundIconEnabled ? 'visible' : 'none';
  for (const id of GROUND_MARKER_LAYER_IDS) {
    if (map?.getLayer(id)) map.setLayoutProperty(id, 'visibility', visible);
  }
}

function applyAircraftLabelVisibility() {
  // Ground labels stay in 2D; in 3D they hide when ground icons are off (3D tags take over).
  const visible = pitch < MAP_TOP_DOWN_PITCH || groundIconEnabled ? 'visible' : 'none';
  if (map?.getLayer(AIRCRAFT_LABEL_LAYER_ID)) {
    map.setLayoutProperty(AIRCRAFT_LABEL_LAYER_ID, 'visibility', visible);
  }
}

function applyFlightPathsVisibility() {
  if (!trajectoryLinesEnabled) {
    if (map?.getLayer(FLIGHT_PATHS_LAYER_ID)) {
      map.setLayoutProperty(FLIGHT_PATHS_LAYER_ID, 'visibility', 'none');
    }
    return;
  }
  // Ground paths hide when 3D nose paths are active in a pitched view.
  const use3D = effectiveTrajectoryFrom3D();
  const visible = use3D ? 'none' : 'visible';
  if (map?.getLayer(FLIGHT_PATHS_LAYER_ID)) {
    map.setLayoutProperty(FLIGHT_PATHS_LAYER_ID, 'visibility', visible);
  }
}

function applyVesselsVisibility() {
  const visible = vesselsEnabled ? 'visible' : 'none';
  for (const id of VESSEL_LAYER_IDS) {
    if (map?.getLayer(id)) map.setLayoutProperty(id, 'visibility', visible);
  }
}

function applyAircraftLayersVisibility() {
  const visible = aircraftEnabled ? 'visible' : 'none';
  const aircraftLayers = [
    'aircraft-body',
    'aircraft-icon',
    'aircraft-selected',
    'aircraft-label',
    'flight-paths',
    'flight-trail',
    'flight-trail-glow',
    ...ALTITUDE_LAYER_IDS,
  ];
  for (const id of aircraftLayers) {
    if (map?.getLayer(id)) map.setLayoutProperty(id, 'visibility', visible);
  }
  if (map?.getLayer(AIRCRAFT_MODELS_LAYER_ID)) {
    try {
      map.setLayoutProperty(AIRCRAFT_MODELS_LAYER_ID, 'visibility', visible);
    } catch {
      // custom layer
    }
  }
}

function notifyGroundIcon() {
  for (const fn of groundIconListeners) fn(groundIconEnabled);
}

function onPitchModeChange(prevPitch: number, nextPitch: number) {
  const wasTopDown = prevPitch < MAP_TOP_DOWN_PITCH;
  const isTopDown = nextPitch < MAP_TOP_DOWN_PITCH;
  if (wasTopDown && !isTopDown) {
    // Entering 3D: hide ground icons and sync the Layers panel toggle.
    if (groundIconEnabled) {
      groundIconAutoDisabledFor3D = true;
      groundIconEnabled = false;
      notifyTrajectorySource();
      notifyGroundIcon();
    }
    return;
  }
  if (!wasTopDown && isTopDown && groundIconAutoDisabledFor3D && !groundIconEnabled) {
    // Leaving 3D after an auto-off: restore the toggle unless the user changed it in 3D.
    groundIconAutoDisabledFor3D = false;
    groundIconEnabled = true;
    notifyTrajectorySource();
    notifyGroundIcon();
  } else if (isTopDown) {
    groundIconAutoDisabledFor3D = false;
  }
}

function apply3DLayerVisibility() {
  applyHeightHologramsVisibility();
  applyAircraft3DModelsVisibility();
  applyGroundIconVisibility();
  applyAircraftLabelVisibility();
  applyFlightPathsVisibility();
}

export const mapBridge = {
  setMap(m: maplibregl.Map | null) {
    map = m;
    if (!m) {
      bearing = 0;
      pitch = 0;
      notifyBearing();
      notifyPitch();
    }
  },
  zoomIn() {
    map?.zoomIn({ duration: 200 });
  },
  zoomOut() {
    map?.zoomOut({ duration: 200 });
  },
  setBearing(b: number) {
    bearing = b;
    notifyBearing();
  },
  getBearing() {
    return bearing;
  },
  subscribeBearing(fn: (b: number) => void) {
    bearingListeners.add(fn);
    fn(bearing);
    return () => {
      bearingListeners.delete(fn);
    };
  },
  resetNorth() {
    map?.easeTo({ bearing: 0, duration: 300, essential: true });
  },
  setPitch(p: number) {
    const prevPitch = pitch;
    pitch = p;
    onPitchModeChange(prevPitch, p);
    notifyPitch();
    apply3DLayerVisibility();
    notifyTrajectorySource();
  },
  getPitch() {
    return pitch;
  },
  subscribePitch(fn: (p: number) => void) {
    pitchListeners.add(fn);
    fn(pitch);
    return () => {
      pitchListeners.delete(fn);
    };
  },
  isTopDown(p = pitch) {
    return p < MAP_TOP_DOWN_PITCH;
  },
  /** Floating 3D tags beside models when ground icons are hidden in a pitched view. */
  use3DLabels(p = pitch) {
    return p >= MAP_TOP_DOWN_PITCH && !groundIconEnabled && aircraft3DModelsEnabled;
  },
  /** 3D trajectory lines from the aircraft nose in a pitched view. */
  use3DFlightPaths(p = pitch) {
    return effectiveTrajectoryFrom3D(p);
  },
  getTrajectoryFrom3DAircraft() {
    return trajectoryFrom3DAircraft;
  },
  /** Effective trajectory source after layer overrides (for UI display). */
  getEffectiveTrajectoryFrom3D(p = pitch) {
    return effectiveTrajectoryFrom3D(p);
  },
  getTrajectorySourceOverride(p = pitch): TrajectorySourceOverride {
    return trajectorySourceOverride(p);
  },
  isTrajectorySourceToggleEnabled(p = pitch) {
    return trajectorySourceOverride(p) === null;
  },
  setTrajectoryFrom3DAircraft(from3D: boolean) {
    trajectoryFrom3DAircraft = from3D;
    applyFlightPathsVisibility();
    notifyTrajectorySource();
  },
  subscribeTrajectorySource(fn: () => void) {
    trajectorySourceListeners.add(fn);
    fn();
    return () => {
      trajectorySourceListeners.delete(fn);
    };
  },
  /** Screen-space picking against 3D models when pitched and models are enabled. */
  canPick3DModels(p = pitch) {
    return p >= MAP_TOP_DOWN_PITCH && aircraft3DModelsEnabled;
  },
  enter3D() {
    map?.easeTo({ pitch: MAP_MAX_PITCH, duration: 400, essential: true });
  },
  enter2D() {
    map?.easeTo({ pitch: 0, duration: 400, essential: true });
  },
  setPitchTarget(targetPitch: number, animate = false) {
    if (!map) return;
    const p = Math.max(0, Math.min(MAP_MAX_PITCH, targetPitch));
    if (animate) map.easeTo({ pitch: p, duration: 250, essential: true });
    else map.setPitch(p);
  },
  togglePitchView() {
    if (mapBridge.isTopDown()) mapBridge.enter3D();
    else mapBridge.enter2D();
  },
  setTopographyEnabled(enabled: boolean) {
    topographyEnabled = enabled;
    applyTopographyVisibility();
    notifyTopography();
  },
  getTopographyEnabled() {
    return topographyEnabled;
  },
  subscribeTopography(fn: (enabled: boolean) => void) {
    topographyListeners.add(fn);
    fn(topographyEnabled);
    return () => {
      topographyListeners.delete(fn);
    };
  },
  syncTopographyLayer() {
    applyTopographyVisibility();
  },
  setHeightHologramsEnabled(enabled: boolean) {
    heightHologramsEnabled = enabled;
    applyHeightHologramsVisibility();
    notifyHeightHolograms();
  },
  getHeightHologramsEnabled() {
    return heightHologramsEnabled;
  },
  subscribeHeightHolograms(fn: (enabled: boolean) => void) {
    heightHologramsListeners.add(fn);
    fn(heightHologramsEnabled);
    return () => {
      heightHologramsListeners.delete(fn);
    };
  },
  syncHeightHologramsLayer() {
    applyHeightHologramsVisibility();
  },
  setAircraft3DModelsEnabled(enabled: boolean) {
    aircraft3DModelsEnabled = enabled;
    applyAircraft3DModelsVisibility();
    applyAircraftLabelVisibility();
    applyFlightPathsVisibility();
    notifyTrajectorySource();
    for (const fn of aircraft3DModelsListeners) fn(aircraft3DModelsEnabled);
  },
  getAircraft3DModelsEnabled() {
    return aircraft3DModelsEnabled;
  },
  subscribeAircraft3DModels(fn: (enabled: boolean) => void) {
    aircraft3DModelsListeners.add(fn);
    fn(aircraft3DModelsEnabled);
    return () => {
      aircraft3DModelsListeners.delete(fn);
    };
  },
  syncAircraft3DModelsLayer() {
    applyAircraft3DModelsVisibility();
  },
  setGroundIconEnabled(enabled: boolean) {
    groundIconEnabled = enabled;
    groundIconAutoDisabledFor3D = false;
    applyGroundIconVisibility();
    applyAircraftLabelVisibility();
    applyFlightPathsVisibility();
    notifyTrajectorySource();
    notifyGroundIcon();
  },
  getGroundIconEnabled() {
    return groundIconEnabled;
  },
  subscribeGroundIcon(fn: (enabled: boolean) => void) {
    groundIconListeners.add(fn);
    fn(groundIconEnabled);
    return () => {
      groundIconListeners.delete(fn);
    };
  },
  syncGroundIconLayer() {
    applyGroundIconVisibility();
  },
  syncAircraftLabelLayer() {
    applyAircraftLabelVisibility();
  },
  syncFlightPathsLayer() {
    applyFlightPathsVisibility();
  },
  setTrajectoryLinesEnabled(enabled: boolean) {
    trajectoryLinesEnabled = enabled;
    applyFlightPathsVisibility();
    notifyTrajectorySource();
    for (const fn of trajectoryLinesListeners) fn(trajectoryLinesEnabled);
  },
  getTrajectoryLinesEnabled() {
    return trajectoryLinesEnabled;
  },
  subscribeTrajectoryLines(fn: (enabled: boolean) => void) {
    trajectoryLinesListeners.add(fn);
    fn(trajectoryLinesEnabled);
    return () => {
      trajectoryLinesListeners.delete(fn);
    };
  },
  setVesselsEnabled(enabled: boolean) {
    vesselsEnabled = enabled;
    applyVesselsVisibility();
    for (const fn of vesselsListeners) fn(vesselsEnabled);
  },
  getVesselsEnabled() {
    return vesselsEnabled;
  },
  subscribeVessels(fn: (enabled: boolean) => void) {
    vesselsListeners.add(fn);
    fn(vesselsEnabled);
    return () => {
      vesselsListeners.delete(fn);
    };
  },
  syncVesselsLayer() {
    applyVesselsVisibility();
  },
  setAircraftEnabled(enabled: boolean) {
    aircraftEnabled = enabled;
    applyAircraftLayersVisibility();
    for (const fn of aircraftListeners) fn(aircraftEnabled);
  },
  getAircraftEnabled() {
    return aircraftEnabled;
  },
  subscribeAircraft(fn: (enabled: boolean) => void) {
    aircraftListeners.add(fn);
    fn(aircraftEnabled);
    return () => {
      aircraftListeners.delete(fn);
    };
  },
  syncAircraftLayers() {
    applyAircraftLayersVisibility();
  },
};
